import request from '@/utils/request'

// Cane audio packs: the sound sets the app writes to the cane's flash.
// Backend: /api/v2/admin/cane-audio (request baseURL already includes /api/v2).

export type CaneAudioSlot = 'female' | 'female2' | 'male' | 'male2'
export type CaneAudioPackKind = 'language' | 'generic' | 'english_primary'

export interface CaneAudioPack {
  id: number
  kind: CaneAudioPackKind
  languageCode: string | null
  slot: CaneAudioSlot | null
  version: string
  objectPath: string
  url: string
  size: number
  sha256: string
  isActive: boolean
  uploadedBy: string | null
  note: string | null
  createdAt: string
}

export interface CaneAudioSlotView {
  slot: CaneAudioSlot
  ttsVoiceName: string | null
  activePack: CaneAudioPack | null
  versionCount: number
}

export interface CaneAudioLanguage {
  code: string
  displayName: string
  backendKey: string
  otaFolder: string
  manifestPrefix: string
  synthesisCode: string
  aliases: string[]
  voices: Partial<Record<CaneAudioSlot, string>>
  sortOrder: number
  isActive: boolean
  updatedAt: string
  slots: CaneAudioSlotView[]
}

export interface CaneAudioSpecialPack {
  activePack: CaneAudioPack | null
  versionCount: number
}

export interface CaneAudioOverview {
  generic: CaneAudioSpecialPack
  englishPrimary: CaneAudioSpecialPack
  slotOrder: CaneAudioSlot[]
  languages: CaneAudioLanguage[]
}

export const getCaneAudioOverview = () =>
  request({ url: '/admin/cane-audio/languages', method: 'get' })

export const createCaneAudioLanguage = (data: Partial<CaneAudioLanguage>) =>
  request({ url: '/admin/cane-audio/languages', method: 'post', data })

export const updateCaneAudioLanguage = (code: string, data: Partial<CaneAudioLanguage>) =>
  request({ url: `/admin/cane-audio/languages/${encodeURIComponent(code)}`, method: 'put', data })

export const listCaneAudioPacks = (params: { kind?: CaneAudioPackKind, languageCode?: string, slot?: string }) =>
  request({ url: '/admin/cane-audio/packs', method: 'get', params })

export const updateCaneAudioPack = (id: number, data: { isActive?: boolean, note?: string | null }) =>
  request({ url: `/admin/cane-audio/packs/${id}`, method: 'put', data })

// The zip is checked on the server the way the app checks it; a pack that
// fails never reaches the bucket. The server's message says what was wrong.
export const uploadCaneAudioPack = (fields: {
  kind: CaneAudioPackKind
  languageCode?: string
  slot?: string
  version: string
  note?: string
}, file: File) => {
  const form = new FormData()
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, String(v)) })
  form.append('file', file, file.name)
  return request({
    url: '/admin/cane-audio/packs/upload',
    method: 'post',
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  })
}
